// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { color } from "@hardwoods/XDL/Utilities";
import { maxLengthAttr, qtyRegex } from "@hardwoods/XDL/Utilities/validation";
import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    editable?: boolean;
    showLabel?: boolean;
    showUOM?: boolean;
    onQtyOrderedChange?: (qtyOrdered: string) => void;
    extendedStyles?: CartLineQuantityStyles;
    label?: string;
    qtyValue?: string;
    unitOfMeasureDisplay?: string;
    disabled?: boolean;
}

type Props = OwnProps;

export interface CartLineQuantityStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
    unitOfMeasure?: InjectableCss;
    quantityWrapper?: InjectableCss;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    editableQuantityTextField: {
        labelProps: {
            variant: "legend",
        },
        cssOverrides: {
            inputSelect: css`
                width: 65px;
                background-color: ${color("common.ui3")};
                padding: 4px 4px 4px 8px !important;
                border-radius: 4px !important;

                &::-webkit-inner-spin-button,
                &::-webkit-outer-spin-button {
                    opacity: 1;
                }

                /* Firefox */
                &[type="number"] {
                    -moz-appearance: textfield;
                }
            `,
        },
    },
    quantityWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    unitOfMeasure: {
        css: css`
            margin-left: 8px;
        `,
    },
};

const XngQuantity: FC<Props> = ({
    qtyValue,
    editable,
    label,
    disabled,
    showLabel,
    showUOM,
    unitOfMeasureDisplay,
    onQtyOrderedChange = () => {},
    extendedStyles,
}) => {
    const localOnQtyOrderedChange = (value: string) => {
        const inputQty = parseFloat(value);
        if (Number.isNaN(inputQty)) {
            return;
        }

        onQtyOrderedChange(`${inputQty}`);
    };

    // XNG-Changes: prevent add decimal number
    const [value, setValue] = useState(qtyValue || "1");

    useEffect(() => {
        if (qtyValue) {
            setValue(qtyValue);
        }
    }, [qtyValue]);

    const changeHandler = (event: any) => {
        const value = event.target.value;
        // <xng-change: check the value and eliminate invalid chars>
        let transformedValue = value;
        if (value) {
            transformedValue = value.replace(qtyRegex, "") || "1";
            event.target.value = transformedValue;
        }
        // </xng-change>
        setValue(transformedValue);
        event.target.value = transformedValue;
        accessibleChangeHandler(event);
    };

    const {
        value: accessibleValue,
        changeHandler: accessibleChangeHandler,
        keyDownHandler,
        blurHandler,
    } = useAccessibleSubmit(qtyValue || "1", localOnQtyOrderedChange);

    const [styles] = React.useState(() => mergeToNew(cartLineQuantityStyles, extendedStyles));

    if (editable) {
        return (
            <StyledWrapper {...styles.quantityWrapper}>
                <TextField
                    {...styles.editableQuantityTextField}
                    type="text"
                    maxLength={maxLengthAttr.qty}
                    label={showLabel && (label || translate("QTY"))}
                    value={value}
                    disabled={disabled}
                    onChange={changeHandler}
                    onKeyDown={keyDownHandler}
                    onBlur={blurHandler}
                    data-test-selector="cartline_qty"
                />
                {showUOM && unitOfMeasureDisplay && (
                    <StyledWrapper {...styles.unitOfMeasure} className="qty-UOM">
                        {unitOfMeasureDisplay}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    }

    return (
        <SmallHeadingAndText
            {...styles.readOnlyQuantityText}
            heading={showLabel ? label || translate("QTY") : ""}
            text={qtyValue || ""}
        />
    );
};

XngQuantity.defaultProps = {
    editable: false,
    showLabel: false,
    showUOM: false,
    qtyValue: "1",
};

export default XngQuantity;
